// frontend/packages/business-console/src/hooks/useTenantFeatures.js
//
// This tenant's client-audience feature flags (GET /v2/features) as a
// { key: enabled } map, fetched once per business and shared by every caller.
// Used by the sidebar to hide nav items whose `feature` is off, so an opt-in
// page (for example Site alerts) only appears for tenants who have it.
//
// Returns { loading, features }. On error, features is {} (every gated item
// stays hidden, which is the safe direction for an opt-in feature).

import { useContext, useEffect, useState } from 'react';

import { BusinessContext } from '../contexts/BusinessContext';
import { getMyFeatures } from '../services/featuresService';

const cache = new Map(); // businessId -> Promise<{ [key]: boolean }>

function load(businessKey) {
  if (!cache.has(businessKey)) {
    cache.set(
      businessKey,
      getMyFeatures()
        .then((list) =>
          Object.fromEntries(list.map((f) => [f.key, Boolean(f.enabled)]))
        )
        .catch(() => {
          cache.delete(businessKey); // retry on the next mount
          return {};
        })
    );
  }
  return cache.get(businessKey);
}

export default function useTenantFeatures() {
  const { business } = useContext(BusinessContext);
  const businessKey = business?._id || business?.id || business?.slug || '';
  const [state, setState] = useState({ loading: true, features: {} });

  useEffect(() => {
    if (!businessKey) return undefined;
    let alive = true;
    load(businessKey).then((features) => {
      if (alive) setState({ loading: false, features });
    });
    return () => {
      alive = false;
    };
  }, [businessKey]);

  return state;
}
